<template>
  <app-content class="user-edit">
    <step-by-step
      class="user-edit__step-by-step"
      ref="stepcmp"
      :steps="steps"
      :loading="loading"
      :current-step.sync="step"
      :conclude-button-text="$t('general.save')"
      @conclude="save()"
    >
      <personal-data
        slot="content:personal-data"
        :user="user"
        :current-user-email="currentUserEmail"
        @can-forward-changed="personalDataCanForward = $event"
      >
      </personal-data>
      <access-data
        slot="content:access-data"
        :user="user"
        @can-forward-changed="accessDataCanForward = $event"
      >
      </access-data>
      <permissions
        slot="content:permissions"
        :user="user"
      >
      </permissions>
      <template slot="bottomPreviousPanel">
        <b-button
          v-if="canCancel"
          variant="secondary"
          @click="cancel()"
        >
          {{ $t('general.cancel') }}
        </b-button>
      </template>
    </step-by-step>
    <password-reset
      ref="passwordResetModal"
      @ok="goToUserList"
      @send-email="openPasswordModal"
      @send-email-submit="sendPasswordEmail"
    />
  </app-content>
</template>

<script>
import StepByStep from '@/components/stepByStep/index';
import UserService from '@/domain/services/user';
import PersonalData from '@/domain/components/generalPanel/adminPanel/user/editing/PersonalData';
import AccessData from '@/domain/components/generalPanel/adminPanel/user/editing/AccessData';
import Permissions from '@/domain/components/generalPanel/adminPanel/user/editing/Permissions';
import PasswordReset from '@/domain/components/generalPanel/adminPanel/user/editing/PasswordReset';
import { mapGetters } from 'vuex';

export default {
  props: {
    userIdForEdit: {
      type: String,
      required: false,
    },
  },
  components: {
    StepByStep,
    PersonalData,
    AccessData,
    Permissions,
    PasswordReset,
  },
  data() {
    return {
      user: {},
      currentUserEmail: null,
      loading: false,
      step: null,
      personalDataCanForward: false,
      accessDataCanForward: false,
      steps: [
        {
          labelPath: 'user-manager.editing.personal-data.title',
          icon: 'fas fa-user',
          name: 'personal-data',
          canForward: () => this.personalDataCanForward,
          onForward: () => this.onForwardPersonalData(),
        },
        {
          name: 'access-data',
          labelPath: 'user-manager.editing.access-data.title',
          icon: 'fas fa-key',
          canForward: () => this.accessDataCanForward,
        },
        {
          name: 'permissions',
          labelPath: 'user-manager.editing.permissions.title',
          icon: 'fas fa-tasks',
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      isOfflineMode: 'isOfflineMode',
      authenticatedUser: 'user',
    }),
    canCancel() {
      return this.step === 0;
    },
  },
  async created() {
    if (!this.userIdForEdit) {
      this.user = await UserService.getNewUser();
    }
  },
  methods: {
    goToUserList() {
      this.$router.push({
        name: 'admin-panel',
      });
    },
    cancel() {
      this.$confirmMessage({
        code: 'MC006',
      }).accepted(() => this.goToUserList());
    },
    onForwardPersonalData() {
      if (this.user.receiveSmsAlerts && !this.user.phone) {
        this.$pushAlert({
          code: 'MA001',
          messageParams: {
            NomeDoCampo: this.$t('user-manager.editing.personal-data.ddd-phone'),
          },
        });
        return false;
      }
      return true;
    },
    showNewPassword(newPassword, eMail) {
      this.$refs.passwordResetModal.show(newPassword, eMail);
    },
    showEmailSentModal(email) {
      this.$refs.passwordResetModal.showEmailSent(email);
    },
    checkTemporaryPassword(newUser = {}) {
      if (!newUser.userPasswordData) {
        this.goToUserList();
        return;
      }
      const passwordData = newUser.userPasswordData || {};
      this.showNewPassword(passwordData.newPassword, passwordData.userEmail);
    },
    saveUser() {
      this.loading = true;
      UserService.saveUser(this.user)
        .then((response) => {
          this.setUser(response.userData);
          this.checkTemporaryPassword(response);
          this.$pushAlert({ code: 'MS006' });
        });
    },
    save() {
      this.$confirmMessage({ code: 'MC011' })
        .accepted(() => {
          this.saveUser();
        });
    },
    setUser(user) {
      this.user = user;
      this.currentUserEmail = this.user?.email;
    },
    openPasswordModal() {
      this.$refs.passwordResetModal.showPasswordConfirmationModal(this.user);
    },
    sendPasswordEmail(authenticatedUser) {
      const sendEmail = true;

      UserService.resetPassword(this.user.id, authenticatedUser.password, sendEmail)
        .then(result => this.showEmailSentModal(result.targetEmail));
    },
  },
  beforeRouteEnter(to, from, next) {
    if (to.params.userId) {
      UserService.getUser(to.params.userId).then((user) => {
        next((vm) => {
          vm.setUser(user);
        });
      });
    } else {
      next();
    }
  },
};
</script>

<style lang="sass" scoped>
@import src/styles/defaults/variables
@import "src/styles/defaults/mobile-first"

.user-edit
  &__step-by-step
    flex: 1
    +media-min-sm()
    +media-min-md()
    +media-min-lg()
</style>
